/**
 * Direct HTTP-based Cognito authentication
 * Uses AWS Cognito API directly without SRP
 * Best practices implemented:
 * - Centralized configuration via constants
 * - Structured logging via logger utility
 * - Comprehensive error handling with descriptive messages
 * - Token management via sessionStorage (AL-213) — tokens are tab-scoped and
 *   automatically cleared when the tab closes, reducing shared-machine risk.
 *   Trade-off: opening a second tab requires logging in again (single-tab
 *   session, same as the C2 verify app's cognitoC2Service.ts pattern).
 */

import { COGNITO_CONFIG } from '../config/constants';
import { getRuntimeConfig } from '../config/runtime';
import { clearSessionCookies } from '../utils/cookieManager';
import { logger } from '../utils/logger';
import { deleteToken, getToken, setToken } from '../utils/tokenStorage';
import socketService from './socketService';
import { wsApi } from './wsApi';

/**
 * Get the effective Cognito config at call-time.
 * Runtime config.json (loaded at app start) takes priority over build-time env
 * so that a deployed build can be re-pointed without a rebuild.
 */
function getEffectiveCognitoConfig() {
  const rc = getRuntimeConfig();
  return {
    USER_POOL_ID: rc?.VITE_AWS_COGNITO_USER_POOL_ID || COGNITO_CONFIG.USER_POOL_ID,
    CLIENT_ID: rc?.VITE_AWS_COGNITO_CLIENT_ID || COGNITO_CONFIG.CLIENT_ID,
    IDP_ENDPOINT: COGNITO_CONFIG.IDP_ENDPOINT, // always the regional endpoint
    AUTH_FLOW: COGNITO_CONFIG.AUTH_FLOW,
  };
}

interface AuthResponse {
  AuthenticationResult?: {
    IdToken: string;
    AccessToken: string;
    RefreshToken: string;
    ExpiresIn: number;
  };
  ChallengeName?: string;
  Session?: string;
  ChallengeParameters?: any;
}

/**
 * Typed authentication failures (AL-470).
 *
 * Cognito returns its exception name in the `__type` field and human prose in
 * `message`. Previously only `message` survived, so the UI matched on substrings
 * and fell back to rendering the raw AWS text. That is brittle (the prose is not
 * a contract) and it leaks account existence, because this pool has
 * PreventUserExistenceErrors disabled and therefore distinguishes an unknown
 * user from a wrong password.
 *
 * Callers should branch on `code` and render their own copy.
 */
export type AuthErrorCode =
  | 'INVALID_CREDENTIALS'
  | 'USER_NOT_FOUND'
  | 'USER_NOT_CONFIRMED'
  /**
   * The account cannot be sent a reset code — no registered or verified email.
   * AL-472: kept apart from PASSWORD_POLICY because it is a statement about an
   * account that exists, and the reset flow has to absorb it rather than
   * repeat it.
   */
  | 'RESET_NOT_ALLOWED'
  | 'PASSWORD_POLICY'
  | 'CHALLENGE_EXPIRED'
  | 'CODE_MISMATCH'
  | 'CODE_EXPIRED'
  | 'LIMIT_EXCEEDED'
  | 'NETWORK'
  | 'UNKNOWN';

export class AuthError extends Error {
  readonly code: AuthErrorCode;
  /** Raw Cognito detail. For logging only — never render this to a user. */
  readonly raw?: string;

  constructor(code: AuthErrorCode, raw?: string) {
    super(code);
    this.name = 'AuthError';
    this.code = code;
    this.raw = raw;
  }
}

/** Map a Cognito error response onto an AuthErrorCode. */
function toAuthErrorCode(data: any): AuthErrorCode {
  // `__type` looks like "NotAuthorizedException" or a full ARN-ish prefix.
  const type: string = String(data?.__type ?? '').split('#').pop() ?? '';
  const message: string = String(data?.message ?? '');

  switch (type) {
    case 'NotAuthorizedException':
      // Cognito uses this for a wrong password AND for an expired temporary
      // password, with no field distinguishing them. Callers word it to cover
      // both rather than guessing.
      return 'INVALID_CREDENTIALS';
    case 'UserNotFoundException':
      return 'USER_NOT_FOUND';
    case 'UserNotConfirmedException':
      return 'USER_NOT_CONFIRMED';
    case 'InvalidPasswordException':
      return 'PASSWORD_POLICY';
    case 'InvalidParameterException':
      // Cognito raises this for two unrelated things, so test the narrower one
      // first. ForgotPassword on an account with no verified address returns
      // "Cannot reset password for the user as there is no registered/verified
      // email or phone_number" — which contains the word "password" and was
      // therefore being reported to the user as a password-complexity failure
      // on a form with no password on it (AL-472).
      if (/no registered\/verified|cannot reset password/i.test(message)) {
        return 'RESET_NOT_ALLOWED';
      }
      // Otherwise: a malformed password on the challenge response.
      return /password/i.test(message) ? 'PASSWORD_POLICY' : 'UNKNOWN';
    case 'CodeMismatchException':
      return 'CODE_MISMATCH';
    case 'ExpiredCodeException':
      return 'CODE_EXPIRED';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'LIMIT_EXCEEDED';
    default:
      return 'UNKNOWN';
  }
}

/** Build an AuthError from a failed Cognito response body. */
function authErrorFrom(data: any): AuthError {
  const code = toAuthErrorCode(data);
  const raw = data?.__type ? `${data.__type}: ${data?.message ?? ''}` : data?.message;
  logger.warn('Cognito request failed', { code, raw });
  return new AuthError(code, raw);
}

export interface LoginCredentials {
  username: string;  // Can be username (UUID) or email depending on Cognito config
  password: string;
}

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
}

export interface ChallengeRequired {
  challengeName: string;
  session: string;
  userAttributes?: any;
}

export interface UserProfile {
  id: string;
  email: string;
  firmName: string;
  firmType: 'accounting' | 'real-estate' | 'legal' | 'compliance' | 'other';
  contactPerson: string;
  abn: string;
  phone: string;
}

/**
 * Authenticate user with username and password using USER_PASSWORD_AUTH flow
 * This uses HTTP POST directly to AWS Cognito without SRP
 * Note: Username can be the UUID username or email (if email alias is enabled)
 */
export async function authenticateUser(credentials: LoginCredentials): Promise<AuthTokens | ChallengeRequired> {
  logger.debug('Attempting login with:', credentials.username);

  const cognitoConfig = getEffectiveCognitoConfig();
  const payload = {
    ClientId: cognitoConfig.CLIENT_ID,
    AuthFlow: cognitoConfig.AUTH_FLOW,
    AuthParameters: {
      USERNAME: credentials.username,
      PASSWORD: credentials.password,
    },
  };

  const response = await fetch(cognitoConfig.IDP_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
    },
    body: JSON.stringify(payload),
  });

  const data: AuthResponse = await response.json();

  if (!response.ok) {
    throw authErrorFrom(data);
  }

  // Handle challenges
  if (data.ChallengeName) {
    return {
      challengeName: data.ChallengeName,
      session: data.Session || '',
      userAttributes: data.ChallengeParameters,
    };
  }

  if (!data.AuthenticationResult) {
    throw new Error('No authentication result received');
  }


  return {
    idToken: data.AuthenticationResult.IdToken,
    accessToken: data.AuthenticationResult.AccessToken,
    refreshToken: data.AuthenticationResult.RefreshToken,
    expiresIn: data.AuthenticationResult.ExpiresIn,
  };
}

/**
 * Initiate forgot password flow
 */
export async function initiatePasswordReset(email: string): Promise<void> {
  const cognitoConfig = getEffectiveCognitoConfig();
  const payload = {
    ClientId: cognitoConfig.CLIENT_ID,
    Username: email,
  };

  logger.info('Initiating password reset for:', email);

  const response = await fetch(cognitoConfig.IDP_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.ForgotPassword',
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (!response.ok) {
    throw authErrorFrom(data);
  }

}

/**
 * Confirm forgot password with verification code and new password
 */
export async function confirmPasswordReset(
  email: string,
  verificationCode: string,
  newPassword: string
): Promise<void> {
  const cognitoConfig = getEffectiveCognitoConfig();
  const payload = {
    ClientId: cognitoConfig.CLIENT_ID,
    Username: email,
    ConfirmationCode: verificationCode,
    Password: newPassword,
  };

  logger.debug('Confirming password reset for:', email);

  const response = await fetch(cognitoConfig.IDP_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.ConfirmForgotPassword',
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (!response.ok) {
    throw authErrorFrom(data);
  }

}

/**
 * Sign out user — clears tokens, cached data, and in-memory stores
 */
export function signOut(): void {
  deleteToken('idToken');
  deleteToken('jwtToken'); // Primary token name
  deleteToken('accessToken'); // Legacy name
  deleteToken('refreshToken');
  // One-time cleanup of pre-AL-213 cookie-based tokens, if any remain
  clearSessionCookies();

  // Clear user-specific localStorage data
  localStorage.removeItem('arcaml:assessments');
  localStorage.removeItem('c1OrgId');
  localStorage.removeItem('app_config_cache');
  localStorage.removeItem('arcaml_acknowledged_documents');
  localStorage.removeItem('dismissedUsageWarnings');
  localStorage.removeItem('lastUsageWarningTime');
  localStorage.removeItem('arcaml_onboarding_dismissed');

  // Clear Zustand stores (imported lazily to avoid circular deps)
  try {
    const { useCddRecordsStore } = require('../stores/cddRecordsStore');
    useCddRecordsStore.getState().clearRecords();
  } catch { }
  try {
    const { useInvitationsStore } = require('../stores/invitationsStore');
    useInvitationsStore.getState().clearInvitations();
  } catch { }
}

/**
 * Get stored tokens
 */
export function getStoredTokens(): AuthTokens | null {
  const idToken = getToken('idToken');
  // Access token is stored as 'jwtToken' for consistency with naming convention
  const accessToken = getToken('jwtToken') || getToken('accessToken');
  const refreshToken = getToken('refreshToken');

  if (!idToken || !accessToken) {
    return null;
  }

  return {
    idToken,
    accessToken,
    refreshToken: refreshToken || undefined,
    expiresIn: 3600,
  };
}

/**
 * Store tokens
 */
export function storeTokens(tokens: AuthTokens): void {
  setToken('idToken', tokens.idToken);
  setToken('jwtToken', tokens.accessToken);
  if (tokens.refreshToken) {
    setToken('refreshToken', tokens.refreshToken);
  }
}

/**
 * Clear all stored tokens on logout or failed authentication
 */
export function clearTokens(): void {
  deleteToken('idToken');
  deleteToken('jwtToken');
  deleteToken('accessToken'); // Clear old naming convention too
  deleteToken('refreshToken');
  // One-time cleanup of pre-AL-213 cookie-based tokens, if any remain
  clearSessionCookies();
}

/**
 * Decode JWT token (without verification - for client-side use only)
 */
export function decodeToken(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (err) {
    return null;
  }
}

export interface NewPasswordChallengeInput {
  username: string;
  /** Cognito challenge session returned by InitiateAuth. */
  session: string;
  newPassword: string;
  /** Required user attributes, when the pool asks for any. */
  userAttributes?: Record<string, string>;
}

/**
 * Respond to a NEW_PASSWORD_REQUIRED challenge.
 *
 * AL-470: the temporary password is deliberately NOT passed. Cognito's
 * ChallengeResponses for this challenge needs only USERNAME and NEW_PASSWORD —
 * the Session already represents the accepted first factor — so the caller has
 * no reason to hold the temporary password in memory to reach this point.
 */
export async function respondToNewPasswordChallenge({
  username,
  session,
  newPassword,
  userAttributes,
}: NewPasswordChallengeInput): Promise<AuthTokens> {
  const cognitoConfig = getEffectiveCognitoConfig();
  const payload = {
    ClientId: cognitoConfig.CLIENT_ID,
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    Session: session,
    ChallengeResponses: {
      USERNAME: username,
      NEW_PASSWORD: newPassword,
      ...(userAttributes ?? {}),
    },
  };

  logger.debug('Responding to NEW_PASSWORD_REQUIRED challenge...');

  const response = await fetch(cognitoConfig.IDP_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.RespondToAuthChallenge',
    },
    body: JSON.stringify(payload),
  });

  const data: AuthResponse = await response.json();

  if (!response.ok) {
    throw authErrorFrom(data);
  }

  if (!data.AuthenticationResult) {
    // Either the session expired mid-flow or the pool asked for a further
    // challenge, which this flow does not support.
    throw new AuthError('CHALLENGE_EXPIRED', data.ChallengeName);
  }


  return {
    idToken: data.AuthenticationResult.IdToken,
    accessToken: data.AuthenticationResult.AccessToken,
    refreshToken: data.AuthenticationResult.RefreshToken,
    expiresIn: data.AuthenticationResult.ExpiresIn,
  };
}

/**
 * Extract email from JWT token payload
 */
export function getUserEmailFromToken(token: string): string | null {
  try {
    const decoded = decodeToken(token);
    return decoded?.email || null;
  } catch (err) {
    return null;
  }
}

/**
 * Get user profile by email
 * In production, fetch from your backend API
 */
export function getUserProfile(email: string): UserProfile | null {
  // TODO: Implement real backend profile lookup
  return null;
}

/**
 * Get the currently logged in user profile
 */
export function getCurrentUserProfile(): UserProfile | null {
  const tokens = getStoredTokens();
  if (!tokens) return null;

  const email = getUserEmailFromToken(tokens.idToken);
  if (!email) return null;

  return getUserProfile(email);
}

/**
 * Authenticate WebSocket connection with JWT token
 * Called after successful Cognito login
 * 
 * Flow:
 * 1. User logs in with Cognito (REST call) → gets JWT
 * 2. App sends auth message to WebSocket with JWT token
 * 3. Backend validates JWT and allows subsequent WebSocket calls
 */
/**
 * Authenticate WebSocket connection with JWT token
 * Returns user details on success
 */
export async function authenticateWebSocket(idToken: string): Promise<any> {
  try {
    const config = getRuntimeConfig();


    // Ensure socket is connected before calling wsApi.auth()

    let socket = socketService.getSocket();

    // If socket doesn't exist or is closed, create a new connection
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      socket = socketService.connect();

      if (!socket) {
        throw new Error('Failed to create WebSocket connection');
      }

      // Wait for connection to open
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('WebSocket connection timeout - check URL and server status'));
        }, 10000);

        if (socket!.readyState === WebSocket.OPEN) {
          clearTimeout(timeout);
          resolve();
        } else {
          const handleOpen = () => {
            clearTimeout(timeout);
            socket!.removeEventListener('open', handleOpen);
            socket!.removeEventListener('error', handleError);
            resolve();
          };

          const handleError = (error: any) => {
            clearTimeout(timeout);
            socket!.removeEventListener('open', handleOpen);
            socket!.removeEventListener('error', handleError);
            reject(new Error('WebSocket connection failed'));
          };

          socket!.addEventListener('open', handleOpen);
          socket!.addEventListener('error', handleError);
        }
      });
    } else {
    }

    // Now socket is connected, use wsApi.auth()

    const authResponse = await wsApi.auth(idToken);


    // Persist org ID so it's available throughout the session
    const orgId = authResponse.AuthConfirmation?.orgLabelDisplay ?? authResponse.AuthConfirmation?.organizationId ?? (authResponse as any).AuthConfirmation?.orgIdentifier;
    if (orgId) {
      localStorage.setItem('c1OrgId', orgId);
    }

    return {
      authenticated: true,
      user: authResponse.AuthConfirmation || authResponse,
      ...authResponse
    };
  } catch (error) {
    throw error;
  }
}
